import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { orderService } from '../services/api.service';
import {
  ShoppingBag,
  Search,
  Filter,
  RefreshCw,
  Eye,
  ChevronLeft,
  ChevronRight,
  Package,
  Truck,
  CheckCircle2,
  XCircle,
  Clock,
  User,
  Mail,
  Calendar,
  CreditCard
} from 'lucide-react';

const ITEMS_PER_PAGE = 10;

const Orders = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialCustomerQuery = searchParams.get('customer') || '';

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(initialCustomerQuery);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const res = await orderService.getOrders();
      setOrders(res.orders || []);
    } catch (err) {
      console.warn("Failed to load admin orders:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const handleUpdateStatus = async (id, status) => {
    try {
      const res = await orderService.updateStatus(id, status);
      const updatedOrder = res.order;
      setOrders(prev => prev.map(o => o.id === id ? { ...o, order_status: status, status_history: updatedOrder?.status_history || o.status_history } : o));
    } catch (err) {
      alert("Failed to update order status.");
    }
  };

  // Metrics Calculations
  const totalOrders = orders.length;
  const processingCount = orders.filter(o => (o.order_status || 'Processing').toLowerCase() === 'processing').length;
  const shippedCount = orders.filter(o => o.order_status?.toLowerCase() === 'shipped').length;
  const deliveredCount = orders.filter(o => o.order_status?.toLowerCase() === 'delivered').length;
  const totalRevenue = orders.reduce((sum, o) => sum + Number(o.total_amount || 0), 0);

  // Filtering Logic
  const filteredOrders = orders.filter(o => {
    const term = search.toLowerCase();
    const orderNum = (o.order_number || `#ORD-${o.id}`).toLowerCase();
    const customerName = (o.user_name || o.shipping_address?.full_name || o.shipping_address?.name || 'Customer').toLowerCase();
    const customerEmail = (o.user_email || o.guest_email || '').toLowerCase();

    const matchesSearch =
      orderNum.includes(term) ||
      customerName.includes(term) ||
      customerEmail.includes(term);

    const matchesStatus =
      statusFilter === 'ALL' ||
      (o.order_status || 'Processing').toLowerCase() === statusFilter.toLowerCase();

    return matchesSearch && matchesStatus;
  });

  // Pagination Math
  const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE) || 1;
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const currentOrders = filteredOrders.slice(indexOfFirstItem, indexOfLastItem);

  const renderStatusPill = (status) => {
    const s = status ? status.toLowerCase() : 'processing';
    let bg = '#fff7ed';
    let color = '#c2410c';
    let border = '#ffedd5';

    if (s === 'shipped') {
      bg = '#eff6ff';
      color = '#1d4ed8';
      border = '#bfdbfe';
    } else if (s === 'delivered') {
      bg = '#dcfce7';
      color = '#15803d';
      border = '#bbf7d0';
    } else if (s === 'cancelled') {
      bg = '#fef2f2';
      color = '#b91c1c';
      border = '#fecaca';
    }

    return (
      <span style={{ fontSize: '11.5px', fontWeight: 800, color, background: bg, border: `1px solid ${border}`, padding: '3px 10px', borderRadius: '6px', textTransform: 'capitalize' }}>
        {status || 'Processing'}
      </span>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      
      {/* Top Banner & Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#0f172a', margin: '0 0 4px 0' }}>
            Customer Orders Management ({totalOrders} Total Orders)
          </h2>
          <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
            Track live customer purchases, update order fulfillment status, and monitor revenue.
          </p>
        </div>

        <button onClick={fetchOrders} className="btn-secondary" style={{ width: 'auto', padding: '0 16px', height: '40px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <RefreshCw size={15} />
          <span>Refresh Orders</span>
        </button>
      </div>

      {/* METRICS OVERVIEW CARDS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
        
        <div className="table-card-box" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Orders</span>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#eff6ff', color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ShoppingBag size={16} />
            </div>
          </div>
          <div style={{ fontSize: '26px', fontWeight: 900, color: '#0f172a' }}>{totalOrders}</div>
          <div style={{ fontSize: '12px', color: '#3b82f6', fontWeight: 700, marginTop: '4px' }}>All Placed Customer Orders</div>
        </div>

        <div className="table-card-box" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Processing</span>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#fff7ed', color: '#c2410c', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Clock size={16} />
            </div>
          </div>
          <div style={{ fontSize: '26px', fontWeight: 900, color: '#0f172a' }}>{processingCount}</div>
          <div style={{ fontSize: '12px', color: '#c2410c', fontWeight: 700, marginTop: '4px' }}>Pending Dispatch</div>
        </div>

        <div className="table-card-box" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Shipped</span>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#eff6ff', color: '#1d4ed8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Truck size={16} />
            </div>
          </div>
          <div style={{ fontSize: '26px', fontWeight: 900, color: '#0f172a' }}>{shippedCount}</div>
          <div style={{ fontSize: '12px', color: '#1d4ed8', fontWeight: 700, marginTop: '4px' }}>In Transit</div>
        </div>

        <div className="table-card-box" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Revenue</span>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#ecfdf5', color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircle2 size={16} />
            </div>
          </div>
          <div style={{ fontSize: '26px', fontWeight: 900, color: '#dc2626' }}>£{totalRevenue.toFixed(2)}</div>
          <div style={{ fontSize: '12px', color: '#10b981', fontWeight: 700, marginTop: '4px' }}>Gross Order Sales</div>
        </div>

      </div>

      {/* FILTER & SEARCH BAR */}
      <div className="table-card-box" style={{ padding: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
          
          <div className="header-search-wrapper" style={{ width: '360px', display: 'block' }}>
            <input
              type="text"
              className="header-search-input"
              placeholder="Search by order #, customer name or email..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            />
            <Search className="header-search-icon" size={16} />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Filter size={14} color="#64748b" />
            <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 700 }}>Status:</span>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px 12px', fontSize: '13px', fontWeight: 700, outline: 'none' }}
            >
              <option value="ALL">All Statuses</option>
              <option value="processing">Processing</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

        </div>

        {/* ORDERS TABLE */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#64748b', textAlign: 'left' }}>
                <th style={{ padding: '12px 16px', fontWeight: 800 }}>ORDER #</th>
                <th style={{ padding: '12px 16px', fontWeight: 800 }}>CUSTOMER</th>
                <th style={{ padding: '12px 16px', fontWeight: 800 }}>ITEMS</th>
                <th style={{ padding: '12px 16px', fontWeight: 800 }}>TOTAL AMOUNT</th>
                <th style={{ padding: '12px 16px', fontWeight: 800 }}>STATUS</th>
                <th style={{ padding: '12px 16px', fontWeight: 800 }}>DATE & TIME</th>
                <th style={{ padding: '12px 16px', fontWeight: 800, textAlign: 'right' }}>ACTION</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>Loading customer orders...</td></tr>
              ) : currentOrders.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>No orders found.</td></tr>
              ) : (
                currentOrders.map((o) => {
                  const numStr = o.order_number || `#ORD-${o.id}`;
                  const customerName = o.user_name || o.shipping_address?.full_name || o.shipping_address?.name || 'Customer';
                  const customerEmail = o.user_email || o.guest_email || 'Guest User';
                  const itemsCount = Array.isArray(o.items) ? o.items.length : 1;

                  return (
                    <tr key={o.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '14px 16px', fontWeight: 900, color: '#0f172a' }}>
                        {numStr}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ fontWeight: 800, color: '#0f172a' }}>{customerName}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{customerEmail}</div>
                        {Array.isArray(o.items) && o.items.some(it => Array.isArray(it.selected_addons) && it.selected_addons.length > 0) && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '3px' }}>
                            {o.items.flatMap(it => it.selected_addons || []).map((addon, aIdx) => (
                              <span key={aIdx} style={{ fontSize: '9.5px', fontWeight: 700, color: '#dc2626', background: '#fef2f2', border: '1px solid #fee2e2', padding: '1px 5px', borderRadius: '4px' }}>
                                + {addon.addon_name || addon.name} (+£{Number(addon.addon_price || addon.price || 0).toFixed(2)})
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '14px 16px', fontWeight: 700, color: '#0f172a' }}>
                        {itemsCount} {itemsCount === 1 ? 'Item' : 'Items'}
                      </td>
                      <td style={{ padding: '14px 16px', fontWeight: 900, color: '#dc2626' }}>
                        £{Number(o.total_amount || 0).toFixed(2)}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        {renderStatusPill(o.order_status)}
                      </td>
                      <td style={{ padding: '14px 16px', color: '#64748b', fontSize: '12px' }}>
                        {new Date(o.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                        <button
                          onClick={() => navigate(`/admin/orders/${o.id}`)}
                          style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 800, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                        >
                          <Eye size={14} />
                          <span>View Detail</span>
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
            <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>
              Page {currentPage} of {totalPages}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#ffffff', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 600 }}
              >
                <ChevronLeft size={16} />
                <span>Previous</span>
              </button>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#ffffff', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 600 }}
              >
                <span>Next</span>
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}

      </div>

    </div>
  );
};

export default Orders;
